import { formatDate, formatTimestamp } from '../../utils/dateTime';
import {
  buildEnvelopePrintableAttachments,
  buildPdfApiAttachments,
  dedupeResolvedAttachments,
  isDataBreachLetter,
  normalizeDataBreachProof,
  resolveEffectiveDataBreachProof,
} from '../../utils/disputeEnvelopeAttachments';
import {
  filterPacketAttachmentsForLetter,
  getPersistedPacketAttachmentsFromRecord,
  pdfApiFromPersistedPacket,
  printableFromPersistedPacket,
} from '../../utils/packetAttachmentPersistence';
import {
  getGeneratedLetterDescription,
  getMethodTypeLabel,
  getPackageGroupKey,
  getResolvedMethodLabel,
  packageToLetterTabInput,
} from '../../utils/letterTabLabel';

export const LIFECYCLE_TABS = [
  'all',
  'draft',
  'ready',
  'printing',
  'processed',
  'sent',
  'delivered',
  'completed',
  'cancelled',
  'failed',
];

export const BUREAU_MAILING_ADDRESSES = {
  equifax: {
    name: 'Equifax Information Services LLC',
    address_line_1: 'P.O. Box 740256',
    city: 'Atlanta',
    state: 'GA',
    zip: '30374',
  },
  experian: {
    name: 'Experian',
    address_line_1: 'P.O. Box 4500',
    city: 'Allen',
    state: 'TX',
    zip: '75013',
  },
  transunion: {
    name: 'TransUnion LLC',
    address_line_1: 'P.O. Box 2000',
    city: 'Chester',
    state: 'PA',
    zip: '19016',
  },
};

const STATUS_PRIORITY = {
  ready: 1,
  printing: 2,
  processed: 3,
  sent: 4,
  generated: 5,
  draft: 6,
};

export function normalizeRawStatus(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

/**
 * Resolve which user_id Dispute Center should query for metro2 rounds.
 * Support/admin view must use effective_user_id (same as portal dashboard context).
 */
export function resolveDisputeCenterUserId(user, supportContext, isInSupportMode) {
  const actorUserId = user?.user_id || user?.id || null;
  if (isInSupportMode && supportContext?.effective_user_id) {
    return {
      userId: supportContext.effective_user_id,
      actorUserId,
      source: 'support_effective_user',
    };
  }
  return {
    userId: actorUserId,
    actorUserId,
    source: 'actor_user',
  };
}

/** Strongest child status wins before round.status fallback. */
const LETTER_STATUS_PRIORITY = [
  'cancelled',
  'canceled',
  'failed',
  'error',
  'returned',
  'return_mail',
  'delivered',
  'completed',
  'closed',
  'responded',
  'sent',
  'mailed',
  'in_transit',
  'sent_to_bureau',
  'processed',
  'processed_for_delivery',
  'printing',
  'printed',
  'queued_print',
  'queued',
  'submitted',
  'submitted_to_provider',
  'ready',
  'letters_ready',
  'generated',
  'pending',
  'draft',
];

function canonicalLetterStatus(raw) {
  const normalized = normalizeRawStatus(raw);
  return normalized === 'canceled' ? 'cancelled' : normalized;
}

/** Prefer the strongest letter-level status across generated_letters + package_snapshots fields. */
export function pickStrongestLetterStatus(...sources) {
  const normalized = sources
    .flat()
    .map(canonicalLetterStatus)
    .filter(Boolean);
  if (!normalized.length) return '';

  for (const priority of LETTER_STATUS_PRIORITY) {
    const canonical = priority === 'canceled' ? 'cancelled' : priority;
    if (normalized.includes(canonical) || normalized.includes(priority)) {
      return canonical;
    }
  }
  return normalized[0];
}

export function coalesceLetterStatusFields(letter, matchedPkg) {
  const strongest = pickStrongestLetterStatus(
    letter?.status,
    letter?.letter_status,
    letter?.delivery_status,
    matchedPkg?.status,
    matchedPkg?.letter_status,
    matchedPkg?.delivery_status
  );

  if (strongest) {
    return {
      status: strongest,
      letter_status: strongest,
      delivery_status: strongest,
    };
  }

  return {
    status: letter?.status || matchedPkg?.status,
    letter_status: letter?.letter_status || matchedPkg?.letter_status,
    delivery_status: letter?.delivery_status || matchedPkg?.delivery_status,
  };
}

function resolveLifecycleFromRawStatus(raw, pkg) {
  switch (canonicalLetterStatus(raw)) {
    case 'draft':
    case 'missing':
      return 'draft';
    case 'generated':
      return 'generated';
    case 'ready':
    case 'letters_ready':
      return pkg?.letter_url ? 'ready' : 'generated';
    case 'letters_generated':
      return pkg?.letter_url ? 'ready' : 'generated';
    case 'pending':
      return pkg?.letter_url ? 'ready' : 'draft';
    case 'submitted':
    case 'submitted_to_provider':
      return 'printing';
    case 'queued':
    case 'queued_print':
    case 'printing':
    case 'printed':
      return 'printing';
    case 'processed_for_delivery':
    case 'processed':
      return 'processed';
    case 'mailed':
    case 'sent':
    case 'in_transit':
    case 'sent_to_bureau':
      return 'sent';
    case 'delivered':
      return 'delivered';
    case 'completed':
    case 'closed':
    case 'responded':
      return 'completed';
    case 'cancelled':
      return 'cancelled';
    case 'failed':
    case 'error':
    case 'returned':
    case 'return_mail':
      return 'failed';
    default:
      return null;
  }
}

function letterLevelStatusValues(pkg) {
  const strongest = pickStrongestLetterStatus(
    pkg?.status,
    pkg?.letter_status,
    pkg?.delivery_status
  );
  if (strongest) return [strongest];

  return [pkg?.status, pkg?.letter_status, pkg?.delivery_status]
    .map(canonicalLetterStatus)
    .filter(Boolean)
    .filter((value, index, list) => list.indexOf(value) === index);
}

/** Map backend/raw statuses to lifecycle bucket keys */
export function mapLetterLifecycleStatus(pkg, round) {
  const roundStatus = normalizeRawStatus(round?.status);
  const letterLevel = letterLevelStatusValues(pkg);

  for (const raw of letterLevel) {
    const lifecycle = resolveLifecycleFromRawStatus(raw, pkg);
    if (lifecycle) return lifecycle;
  }

  // Round rollup only when no letter/package child status is present.
  if (!letterLevel.length && roundStatus) {
    const lifecycle = resolveLifecycleFromRawStatus(roundStatus, pkg);
    if (lifecycle) return lifecycle;
  }

  if (!pkg?.letter_url) {
    if (['draft', 'processing'].includes(roundStatus)) return 'draft';
    return 'draft';
  }

  const delivery = canonicalLetterStatus(pkg?.delivery_status);
  if (pkg?.letter_url && (delivery === 'pending' || !delivery)) return 'ready';
  return 'unknown';
}

export function formatMailingAddress(bureauKey) {
  const key = normalizeRawStatus(bureauKey);
  const addr = BUREAU_MAILING_ADDRESSES[key];
  if (!addr) return '—';
  return `${addr.address_line_1}, ${addr.city}, ${addr.state} ${addr.zip}`;
}

export function getRecipientForBureau(bureau) {
  const key = normalizeRawStatus(bureau);
  return BUREAU_MAILING_ADDRESSES[key]?.name || bureau || '—';
}

function countItems(pkg) {
  const findings = pkg?.findings_snapshot || [];
  let accounts = 0;
  let inquiries = 0;
  let pi = 0;
  findings.forEach((f) => {
    const cat = normalizeRawStatus(f?.category || f?.item_type);
    if (cat.includes('inquiry')) inquiries += 1;
    else if (cat.includes('personal')) pi += 1;
    else accounts += 1;
  });
  return { accounts, inquiries, pi, total: findings.length };
}

function bureauKey(bureau) {
  return normalizeRawStatus(bureau);
}

function methodSignature(pkg, round) {
  const input = packageToLetterTabInput(pkg, round);
  return getMethodTypeLabel(input).toLowerCase().replace(/\s+/g, '_');
}

function methodTechnicalKey(pkg) {
  return normalizeRawStatus(
    pkg?.letter_method ||
      pkg?.package_method ||
      pkg?.method_used ||
      pkg?.method ||
      pkg?.family ||
      pkg?.prompt_family ||
      pkg?.context?.method_key ||
      pkg?.context?.method_used ||
      pkg?.context?.method ||
      pkg?.context?.prompt_family
  );
}

function letterHtmlToDataUrl(html) {
  if (!html || typeof html !== 'string') return null;
  if (html.startsWith('data:text/html')) return html;
  try {
    const encoded = btoa(unescape(encodeURIComponent(html)));
    return `data:text/html;base64,${encoded}`;
  } catch {
    return null;
  }
}

/** Map durable generated_letters document to a package-shaped record for normalization. */
export function synthesizePackageFromGeneratedLetter(letter, round, packages = []) {
  const bureau = letter?.bureau || letter?.target_bureau;
  const methodUsed =
    letter?.package_method ||
    letter?.method_used ||
    letter?.method ||
    letter?.family ||
    letter?.prompt_family ||
    letter?.metadata?.method_used ||
    letter?.metadata?.method_key ||
    letter?.metadata?.method ||
    letter?.metadata?.family ||
    letter?.metadata?.prompt_family ||
    '';

  const matchedPkg =
    packages.find(
      (p) =>
        bureauKey(p.target_bureau) === bureauKey(bureau) &&
        (p.letter_method === methodUsed || !p.letter_method)
    ) || packages.find((p) => bureauKey(p.target_bureau) === bureauKey(bureau));

  const html = letter?.letter_html || letter?.html || '';
  const letterUrl = letterHtmlToDataUrl(html) || matchedPkg?.letter_url || null;
  const letterId = letter?.letter_id || letter?.id;
  const stableSyntheticId =
    letterId ||
    [
      letter?.run_id || letter?.generation_run_id || 'run',
      letter?.queue_item_id || letter?.metadata?.queue_item_id || 'queue',
      bureauKey(bureau) || 'bureau',
      normalizeRawStatus(methodUsed || letter?.package_subtype || letter?.metadata?.package_subtype || 'method'),
      normalizeRawStatus(letter?.generated_at || letter?.updated_at || letter?.created_at || 'time'),
    ].join(':');

  const mergedStatus = coalesceLetterStatusFields(letter, matchedPkg);

  return {
    ...(matchedPkg || {}),
    id: stableSyntheticId,
    round_id: round?.id || letter?.round_id || matchedPkg?.round_id,
    target_bureau: bureau,
    letter_method: methodUsed,
    letter_url: letterUrl,
    status: mergedStatus.status,
    letter_status: mergedStatus.letter_status,
    delivery_status: mergedStatus.delivery_status,
    generated_at: letter?.generated_at || matchedPkg?.generated_at,
    updated_at: letter?.updated_at || letter?.generated_at || matchedPkg?.updated_at,
    package_subtype: letter?.package_subtype || matchedPkg?.package_subtype,
    item_types: letter?.item_types || matchedPkg?.item_types,
    findings_snapshot:
      letter?.disputed_items?.length > 0
        ? letter.disputed_items
        : matchedPkg?.findings_snapshot || [],
    context: {
      ...(matchedPkg?.context || {}),
      method_used: methodUsed,
      method_key: methodUsed,
      package_subtype: letter?.package_subtype || matchedPkg?.context?.package_subtype,
      prompt_family: letter?.prompt_family || matchedPkg?.context?.prompt_family,
      item_types: letter?.item_types || matchedPkg?.context?.item_types,
    },
    _source: 'generated_letter',
    _linkedPackageId: matchedPkg?.id || null,
    _letterId: stableSyntheticId,
    _generationRunId: letter?.run_id || letter?.generation_run_id || null,
    _promptFamily: letter?.prompt_family || letter?.metadata?.prompt_family || null,
    identity_support: {
      ...(matchedPkg?.identity_support || {}),
      ...(matchedPkg?.package_identity_support || {}),
      ...(letter?.identity_support || {}),
      ...(letter?.package_identity_support || {}),
    },
    package_identity_support: {
      ...(matchedPkg?.package_identity_support || {}),
      ...(matchedPkg?.identity_support || {}),
      ...(letter?.package_identity_support || {}),
      ...(letter?.identity_support || {}),
    },
    per_letter_attachments: {
      ...(matchedPkg?.per_letter_attachments || {}),
      ...(letter?.per_letter_attachments || {}),
    },
    package_shared_attachments: [
      ...(Array.isArray(matchedPkg?.package_shared_attachments) ? matchedPkg.package_shared_attachments : []),
      ...(Array.isArray(letter?.package_shared_attachments) ? letter.package_shared_attachments : []),
    ],
    data_breach_proof: letter?.data_breach_proof || matchedPkg?.data_breach_proof,
    ssn_approvals: {
      ...(matchedPkg?.ssn_approvals || {}),
      ...(letter?.ssn_approvals || {}),
    },
    attachments: [
      ...(Array.isArray(matchedPkg?.attachments) ? matchedPkg.attachments : []),
      ...(Array.isArray(letter?.attachments) ? letter.attachments : []),
    ],
    resolved_packet_attachments:
      letter?.resolved_packet_attachments ||
      matchedPkg?.resolved_packet_attachments ||
      letter?.final_packet_attachments ||
      matchedPkg?.final_packet_attachments,
    final_packet_attachments:
      letter?.final_packet_attachments ||
      matchedPkg?.final_packet_attachments ||
      letter?.resolved_packet_attachments ||
      matchedPkg?.resolved_packet_attachments,
  };
}

function isNestedLetterRecord(record) {
  if (!record || typeof record !== 'object') return false;
  return Boolean(
    record.letter_id ||
      record.letter_html ||
      record.html ||
      record.package_method ||
      record.method_used
  );
}

/**
 * Collect one package-shaped source per actual generated letter.
 * packageGroupKey is NOT used here — only for bulk selection in the dashboard.
 */
export function collectRoundLetterSources(round, packages = [], generatedLetters = []) {
  const audit = {
    roundId: round?.id,
    packagesFromApi: packages.length,
    generatedLettersFromApi: generatedLetters.length,
    normalizedRows: 0,
    skipped: [],
  };

  const rows = [];
  const seenRowKeys = new Set();

  const addRow = (pkg, source, skipReasonIfDup) => {
    const technicalMethod = methodTechnicalKey(pkg);
    const rowKey = pkg?._letterId
      ? `letter:${pkg._letterId}`
      : pkg?.id
        ? `pkg:${pkg.id}`
        : `fallback:${bureauKey(pkg?.target_bureau)}:${technicalMethod || methodSignature(pkg, round)}`;

    if (seenRowKeys.has(rowKey)) {
      audit.skipped.push({ rowKey, source, reason: skipReasonIfDup || 'duplicate_row_key' });
      return;
    }
    seenRowKeys.add(rowKey);
    rows.push({ pkg, source });
  };

  const allGenerated = [...generatedLetters];

  if (Array.isArray(round?.generated_letters)) {
    allGenerated.push(...round.generated_letters);
  }

  packages.forEach((pkg) => {
    if (Array.isArray(pkg?.letters)) allGenerated.push(...pkg.letters);
    if (Array.isArray(pkg?.generated_letters)) allGenerated.push(...pkg.generated_letters);
  });

  const generatedRows = [];
  allGenerated.forEach((letter) => {
    if (!isNestedLetterRecord(letter)) return;
    const synth = synthesizePackageFromGeneratedLetter(letter, round, packages);
    const rowKey = synth._letterId ? `letter:${synth._letterId}` : null;
    if (rowKey && seenRowKeys.has(rowKey)) {
      audit.skipped.push({ rowKey, source: 'generated_letter', reason: 'duplicate_letter_id' });
      return;
    }
    addRow(synth, 'generated_letter');
    generatedRows.push(synth);
  });

  const coveredByGenerated = (pkg) =>
    generatedRows.some(
      (gen) =>
        bureauKey(gen.target_bureau) === bureauKey(pkg?.target_bureau) &&
        methodTechnicalKey(gen) === methodTechnicalKey(pkg) &&
        Boolean(methodTechnicalKey(gen))
    );

  packages.forEach((pkg) => {
    if (!pkg?.id && !pkg?._id) return;
    const normalizedPkg = { ...pkg, id: pkg.id || pkg._id };
    if (coveredByGenerated(normalizedPkg)) {
      audit.skipped.push({
        id: normalizedPkg.id,
        source: 'package_snapshot',
        reason: 'covered_by_generated_letter_same_bureau_method',
      });
      return;
    }
    addRow(normalizedPkg, 'package_snapshot');
  });

  audit.normalizedRows = rows.length;

  if (process.env.NODE_ENV === 'development') {
    const includedRows = rows.map(({ pkg }) => {
      const letterId = pkg?._letterId || pkg?.id;
      const generatedRunId =
        pkg?._generationRunId ||
        pkg?.run_id ||
        pkg?.generation_run_id ||
        pkg?.rawPackage?.run_id ||
        null;
      const method = methodTechnicalKey(pkg);
      return {
        round_id: round?.id || pkg?.round_id || 'unknown',
        generation_run_id: generatedRunId || 'unknown',
        letter_id: letterId || 'unknown',
        bureau: pkg?.target_bureau || 'unknown',
        method: method || 'unknown',
        package_subtype: pkg?.package_subtype || pkg?.context?.package_subtype || 'unknown',
        prompt_family: pkg?._promptFamily || pkg?.prompt_family || pkg?.context?.prompt_family || 'unknown',
        status: pkg?.letter_status || pkg?.delivery_status || 'unknown',
        included_in_table: true,
        skipped_reason: '',
      };
    });
    const skippedRows = audit.skipped.map((entry) => ({
      round_id: round?.id || 'unknown',
      generation_run_id: entry.generation_run_id || 'unknown',
      letter_id: entry.id || entry.rowKey || 'unknown',
      bureau: entry.bureau || 'unknown',
      method: entry.method || 'unknown',
      package_subtype: entry.package_subtype || 'unknown',
      prompt_family: entry.prompt_family || 'unknown',
      status: entry.status || 'unknown',
      included_in_table: false,
      skipped_reason: entry.reason || 'unknown',
    }));
    // Requested debug audit: one row per generated letter considered for table inclusion
    console.table([...includedRows, ...skippedRows]);
  }
  return { rows, audit };
}

/** Dev-only audit logging for letter visibility debugging. */
export function logDisputeCenterLetterAudit(roundAudits, visibleCount, lifecycleTab) {
  if (process.env.NODE_ENV !== 'development') return;
  const totals = roundAudits.reduce(
    (acc, a) => ({
      packages: acc.packages + (a.packagesFromApi || 0),
      generated: acc.generated + (a.generatedLettersFromApi || 0),
      rows: acc.rows + (a.normalizedRows || 0),
      skipped: acc.skipped + (a.skipped?.length || 0),
    }),
    { packages: 0, generated: 0, rows: 0, skipped: 0 }
  );
  console.info('[DisputeCenter] letter audit', {
    lifecycleTab,
    visibleRows: visibleCount,
    rounds: roundAudits.length,
    ...totals,
    perRound: roundAudits,
  });
}

export function normalizeGeneratedLetter(pkg, round) {
  const bureau = pkg?.target_bureau || round?.target_bureau || '—';
  const lifecycleStatus = mapLetterLifecycleStatus(pkg, round);
  const items = countItems(pkg);
  const methodKey =
    pkg?.letter_method ||
    pkg?.context?.method_key ||
    pkg?.context?.method_used ||
    pkg?.context?.method ||
    '';
  const roundLabel = round?.round_number ? `Round ${round.round_number}` : '—';
  const createdAt = pkg?.generated_at || pkg?.created_at || round?.created_at;
  const updatedAt = pkg?.updated_at || pkg?.generated_at || createdAt;

  return {
    id: pkg?.id,
    roundId: pkg?.round_id || round?.id,
    description: getGeneratedLetterDescription(pkg, round),
    bureau,
    recipient: getRecipientForBureau(bureau),
    address: formatMailingAddress(bureau),
    lifecycleStatus,
    roundNumber: round?.round_number,
    roundLabel,
    method: getResolvedMethodLabel(pkg, round),
    methodKey,
    packageGroupKey: getPackageGroupKey(pkg, round),
    letterType: pkg?.package_type || pkg?.context?.letter_type || '—',
    items,
    itemsSummary: items.total
      ? `${items.accounts} accounts${items.inquiries ? `, ${items.inquiries} inquiries` : ''}${items.pi ? `, ${items.pi} personal info` : ''}`
      : '—',
    createdAt,
    updatedAt,
    createdSort: createdAt ? new Date(createdAt).getTime() : 0,
    updatedSort: updatedAt ? new Date(updatedAt).getTime() : 0,
    letterUrl: pkg?.letter_url,
    deliveryTracking: pkg?.delivery_tracking,
    latestActivityKey: buildActivityKey(lifecycleStatus),
    nextActionKey: buildNextActionKey(lifecycleStatus),
    rawPackage: pkg,
    rawRound: round,
  };
}

function buildActivityKey(status) {
  const map = {
    draft: 'draft',
    generated: 'generated',
    ready: 'ready',
    printing: 'printing',
    processed: 'processed',
    sent: 'sent',
    delivered: 'delivered',
    completed: 'completed',
    cancelled: 'cancelled',
    failed: 'failed',
    unknown: 'unknown',
  };
  return map[status] || 'unknown';
}

function buildNextActionKey(status) {
  const map = {
    draft: 'finishDraft',
    generated: 'reviewGenerated',
    ready: 'reviewReady',
    printing: 'waitPrinting',
    processed: 'waitProcessed',
    sent: 'trackDelivery',
    delivered: 'awaitResponse',
    completed: 'viewSummary',
    cancelled: 'reviewHistory',
    failed: 'reviewFailed',
    unknown: 'reviewLetter',
  };
  return map[status] || 'reviewLetter';
}

export function isLetterDeletable(letter) {
  if (!letter) return false;
  if (!letter.id) return false;
  const raw = letter.rawPackage || {};
  if (raw.is_deleted) return false;
  return true;
}

export function pickDefaultLetter(letters) {
  if (!letters?.length) return null;
  const sorted = [...letters].sort((a, b) => b.createdSort - a.createdSort);

  const pick = (statuses) => {
    const match = sorted.find((l) => statuses.includes(l.lifecycleStatus));
    return match || null;
  };

  return (
    pick(['ready']) ||
    pick(['printing', 'processed']) ||
    pick(['sent']) ||
    pick(['generated', 'draft']) ||
    sorted[0]
  );
}

export function getProgressSteps(letter) {
  if (!letter) return { steps: [], variant: 'normal' };
  const status = letter.lifecycleStatus;

  if (status === 'cancelled') {
    return {
      variant: 'cancelled',
      steps: [
        { key: 'generated', state: 'complete' },
        { key: 'cancelled', state: 'terminal' },
      ],
    };
  }

  if (status === 'failed') {
    return {
      variant: 'failed',
      steps: [
        { key: 'generated', state: 'complete' },
        { key: 'failed', state: 'terminal' },
      ],
    };
  }

  const flow = ['draft', 'ready', 'printing', 'processed', 'sent', 'delivered', 'completed'];
  const index = flow.indexOf(status);
  const effectiveIndex = index >= 0 ? index : status === 'generated' ? 0 : -1;

  return {
    variant: 'normal',
    steps: flow.map((key, i) => {
      if (effectiveIndex < 0) return { key, state: 'upcoming' };
      if (i < effectiveIndex) return { key, state: 'complete' };
      if (i === effectiveIndex) return { key, state: 'current' };
      return { key, state: 'upcoming' };
    }),
  };
}

export function filterLettersByTab(letters, tab) {
  if (tab === 'all') return letters;
  if (tab === 'draft') return letters.filter((l) => ['draft', 'generated'].includes(l.lifecycleStatus));
  return letters.filter((l) => l.lifecycleStatus === tab);
}

export function searchLetters(letters, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return letters;
  return letters.filter((l) => {
    const hay = [
      l.description,
      l.bureau,
      l.recipient,
      l.address,
      l.lifecycleStatus,
      l.method,
      l.roundLabel,
      l.letterType,
    ]
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
}

export function sortLetters(letters, sortKey, columnSort = null) {
  const list = [...letters];

  if (columnSort?.field && columnSort?.dir) {
    const mult = columnSort.dir === 'asc' ? 1 : -1;
    const field = columnSort.field === 'updatedAt' ? 'updatedSort' : 'createdSort';
    list.sort((a, b) => (a[field] - b[field]) * mult);
    return list;
  }

  switch (sortKey) {
    case 'oldest':
      return list.sort((a, b) => a.createdSort - b.createdSort);
    case 'status':
      return list.sort((a, b) => (STATUS_PRIORITY[a.lifecycleStatus] || 99) - (STATUS_PRIORITY[b.lifecycleStatus] || 99));
    case 'bureau':
      return list.sort((a, b) => String(a.bureau).localeCompare(String(b.bureau)));
    case 'newest':
    default:
      return list.sort((a, b) => b.createdSort - a.createdSort);
  }
}

export function paginateLetters(letters, page, perPage) {
  const total = letters.length;
  const pages = Math.max(1, Math.ceil(total / perPage));
  const safePage = Math.min(Math.max(1, page), pages);
  const start = (safePage - 1) * perPage;
  return {
    page: safePage,
    pages,
    total,
    start: total ? start + 1 : 0,
    end: Math.min(start + perPage, total),
    items: letters.slice(start, start + perPage),
  };
}

export function computeSummaryStats(letters) {
  const counts = {};
  LIFECYCLE_TABS.forEach((t) => {
    counts[t] = 0;
  });
  letters.forEach((l) => {
    counts.all += 1;
    if (['draft', 'generated'].includes(l.lifecycleStatus)) counts.draft += 1;
    else if (counts[l.lifecycleStatus] != null) counts[l.lifecycleStatus] += 1;
    else counts.unknown = (counts.unknown || 0) + 1;
  });
  return counts;
}

export function buildOutcomeMatrix(letters) {
  const bureaus = ['Equifax', 'Experian', 'TransUnion'];
  const rows = [
    { key: 'ready', statuses: ['ready', 'generated'] },
    { key: 'inTransit', statuses: ['printing', 'processed', 'sent'] },
    { key: 'delivered', statuses: ['delivered'] },
    { key: 'completed', statuses: ['completed'] },
    { key: 'issue', statuses: ['failed', 'cancelled'] },
  ];

  return rows.map((row) => ({
    key: row.key,
    counts: bureaus.map((bureau) =>
      letters.filter(
        (l) => l.bureau === bureau && row.statuses.includes(l.lifecycleStatus)
      ).length
    ),
  }));
}

export function buildLatestActivity(letters, limit = 6) {
  return [...letters]
    .sort((a, b) => b.updatedSort - a.updatedSort)
    .slice(0, limit)
    .map((l) => ({
      id: l.id,
      titleKey: l.latestActivityKey,
      bureau: l.bureau,
      date: l.updatedAt,
      dateLabel: l.updatedAt ? formatDate(l.updatedAt) : '—',
    }));
}

export function computeNextBestAction(letters, t, DC) {
  const ready = letters.filter((l) => l.lifecycleStatus === 'ready');
  const failed = letters.filter((l) => l.lifecycleStatus === 'failed');
  const sent = letters.filter((l) => ['sent', 'delivered', 'processed', 'printing'].includes(l.lifecycleStatus));

  if (ready.length) {
    return {
      titleKey: 'reviewReady',
      bodyKey: 'reviewReadyBody',
      pills: ['readyAvailable', 'previewBeforeSend'],
    };
  }
  if (failed.length) {
    return {
      titleKey: 'reviewFailed',
      bodyKey: 'reviewFailedBody',
      pills: ['actionNeeded'],
    };
  }
  if (sent.length) {
    return {
      titleKey: 'trackDelivery',
      bodyKey: 'trackDeliveryBody',
      pills: ['inTransit'],
    };
  }
  if (!letters.length) {
    return {
      titleKey: 'noLetters',
      bodyKey: 'noLettersBody',
      pills: ['createFirst'],
    };
  }
  return {
    titleKey: 'reviewReady',
    bodyKey: 'defaultBody',
    pills: ['stayOrganized'],
  };
}

export function formatLetterDate(value) {
  if (!value) return '—';
  return formatTimestamp(value, { includeSeconds: false });
}

function asAttachmentArray(value) {
  return Array.isArray(value) ? value : [];
}

const STANDARD_IDENTITY_SLOTS = new Set([
  'proof_of_address',
  'government_id',
  'ssn_card',
  'ssn_support',
  'data_breach_proof',
]);

function mergeIdentitySupportSlots(...sources) {
  const merged = {};
  sources.forEach((source) => {
    if (!source || typeof source !== 'object' || Array.isArray(source)) return;
    Object.entries(source).forEach(([key, value]) => {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return;
      if (STANDARD_IDENTITY_SLOTS.has(key)) {
        merged[key] = { ...(merged[key] || {}), ...value };
      }
    });
  });
  return merged;
}

function normalizeFlatAttachmentType(att) {
  const raw = String(att?.type || att?.category || att?.document_type || '').toLowerCase();
  if (raw === 'ssn_support') return 'ssn_card';
  if (raw === 'identity_verification') return 'government_id';
  return raw;
}

function ingestFlatAttachmentsIntoContext(flatAttachments, ctx) {
  flatAttachments.forEach((att) => {
    if (!att || att.attached === false || att.include_in_package === false) return;
    const type = normalizeFlatAttachmentType(att);

    if (type === 'proof_of_address') {
      ctx.packageIdentitySupport.proof_of_address =
        ctx.packageIdentitySupport.proof_of_address || att;
      return;
    }
    if (type === 'government_id') {
      ctx.packageIdentitySupport.government_id =
        ctx.packageIdentitySupport.government_id || att;
      return;
    }
    if (type === 'ssn_card') {
      ctx.packageIdentitySupport.ssn_card = ctx.packageIdentitySupport.ssn_card || att;
      ctx.packageIdentitySupport.ssn_support = ctx.packageIdentitySupport.ssn_support || att;
      return;
    }
    if (type === 'data_breach_proof') {
      ctx.dataBreachProof = ctx.dataBreachProof || att;
      return;
    }

    ctx.packageSharedAttachments.push(att);
  });
}

function resolvePerLetterAttachmentList(perLetterAttachments, letter, letterId) {
  if (!perLetterAttachments || typeof perLetterAttachments !== 'object') return [];

  const candidateIds = [
    letterId,
    letter?.id,
    letter?.rawPackage?._letterId,
    letter?.rawPackage?._linkedPackageId,
    letter?.rawPackage?.id,
    letter?.rawPackage?.letter_id,
  ].filter(Boolean);

  const collected = [];
  const seenListKeys = new Set();

  candidateIds.forEach((id) => {
    const list = perLetterAttachments[id];
    if (!Array.isArray(list) || seenListKeys.has(id)) return;
    seenListKeys.add(id);
    collected.push(...list);
  });

  const bureauToken = bureauKey(letter?.bureau || letter?.rawPackage?.target_bureau);
  if (bureauToken) {
    Object.entries(perLetterAttachments).forEach(([key, list]) => {
      if (!Array.isArray(list) || seenListKeys.has(key)) return;
      if (String(key).toLowerCase().includes(bureauToken)) {
        seenListKeys.add(key);
        collected.push(...list);
      }
    });
  }

  return collected;
}

function collectAllFlatAttachmentArrays(letter) {
  const pkg = letter?.rawPackage || {};
  const round = letter?.rawRound || {};
  const meta = pkg.metadata || {};

  return [
    ...asAttachmentArray(pkg.attachments),
    ...asAttachmentArray(meta.attachments),
    ...asAttachmentArray(round.attachments),
    ...asAttachmentArray(letter?.attachments),
  ];
}

/** Read attachment wizard state persisted on generated_letters / package_snapshots / round records. */
export function extractLetterAttachmentContext(letter) {
  const pkg = letter?.rawPackage || {};
  const round = letter?.rawRound || {};
  const meta = pkg.metadata || {};

  const identitySlots = mergeIdentitySupportSlots(
    round.identity_support,
    round.package_identity_support,
    pkg.identity_support,
    pkg.package_identity_support,
    meta.identity_support,
    meta.package_identity_support,
    pkg.context?.identity_support,
    pkg.context?.package_identity_support
  );

  const packageIdentitySupport = { ...identitySlots };

  if (!packageIdentitySupport.proof_of_address && (pkg.proof_of_address || meta.proof_of_address)) {
    packageIdentitySupport.proof_of_address = pkg.proof_of_address || meta.proof_of_address;
  }
  if (!packageIdentitySupport.government_id && (pkg.government_id || meta.government_id)) {
    packageIdentitySupport.government_id = pkg.government_id || meta.government_id;
  }
  if (!packageIdentitySupport.ssn_card && (pkg.ssn_card || pkg.ssn_support || meta.ssn_card)) {
    packageIdentitySupport.ssn_card = pkg.ssn_card || meta.ssn_card || pkg.ssn_support;
  }
  if (!packageIdentitySupport.ssn_support && (pkg.ssn_support || pkg.ssn_card || meta.ssn_support)) {
    packageIdentitySupport.ssn_support = pkg.ssn_support || meta.ssn_support || pkg.ssn_card;
  }

  const perLetterAttachments = {
    ...(round.per_letter_attachments || {}),
    ...(round.perLetterAttachments || {}),
    ...(pkg.per_letter_attachments || {}),
    ...(pkg.perLetterAttachments || {}),
    ...(meta.per_letter_attachments || {}),
  };

  const packageSharedAttachments = [
    ...asAttachmentArray(round.package_shared_attachments),
    ...asAttachmentArray(round.packageSharedAttachments),
    ...asAttachmentArray(pkg.package_shared_attachments),
    ...asAttachmentArray(pkg.packageSharedAttachments),
    ...asAttachmentArray(meta.package_shared_attachments),
  ];

  let dataBreachProof =
    pkg.data_breach_proof ||
    pkg.dataBreachProof ||
    meta.data_breach_proof ||
    round.data_breach_proof ||
    round.dataBreachProof ||
    null;

  const ssnApprovals = {
    ...(round.ssn_approvals || {}),
    ...(round.ssnApprovals || {}),
    ...(pkg.ssn_approvals || {}),
    ...(pkg.ssnApprovals || {}),
    ...(meta.ssn_approvals || {}),
  };

  const ctx = {
    packageIdentitySupport,
    perLetterAttachments,
    packageSharedAttachments,
    dataBreachProof,
    ssnApprovals,
  };

  ingestFlatAttachmentsIntoContext(collectAllFlatAttachmentArrays(letter), ctx);

  const letterId = pkg._letterId || letter?.id;
  const letterSpecific = resolvePerLetterAttachmentList(perLetterAttachments, letter, letterId);
  if (letterSpecific.length) {
    ctx.perLetterAttachments = {
      ...perLetterAttachments,
      [letterId]: letterSpecific,
    };
    ingestFlatAttachmentsIntoContext(letterSpecific, ctx);
  }

  return ctx;
}

/** Letter/envelope shape for shared attachment resolution (matches Step 8 send/download). */
export function buildAttachmentLetterTarget(letter) {
  const pkg = letter?.rawPackage || {};
  return {
    ...pkg,
    bureau: letter?.bureau || pkg.target_bureau,
    target_bureau: letter?.bureau || pkg.target_bureau,
    letter_id: pkg._letterId || letter?.id,
    id: letter?.id,
    letter_method: pkg.letter_method || letter?.methodKey,
    package_method: pkg.letter_method || letter?.methodKey,
    method: letter?.method,
    method_key: letter?.methodKey,
    package_subtype: pkg.package_subtype || pkg.context?.package_subtype,
    prompt_family: pkg._promptFamily || pkg.prompt_family || pkg.context?.prompt_family,
  };
}

/** Resolve printable + PostGrid attachments for Dispute Center preview (no regeneration). */
export function resolveLetterPreviewPacket(letter) {
  const pkg = letter?.rawPackage || {};
  const target = buildAttachmentLetterTarget(letter);
  const letterId = letter?.rawPackage?._letterId || letter?.id;

  const persisted = getPersistedPacketAttachmentsFromRecord(pkg);
  if (persisted?.length) {
    const filtered = filterPacketAttachmentsForLetter(persisted, target);
    const printable = dedupeResolvedAttachments(printableFromPersistedPacket(filtered));
    return {
      resolvedAttachments: printable,
      pdfApiAttachments: pdfApiFromPersistedPacket(filtered),
      letterId,
      letterTarget: target,
      isDataBreach: isDataBreachLetter(target),
      fromPersisted: true,
    };
  }

  const ctx = extractLetterAttachmentContext(letter);
  let dataBreachProof = ctx.dataBreachProof;
  if (isDataBreachLetter(target)) {
    dataBreachProof = resolveEffectiveDataBreachProof(
      dataBreachProof || normalizeDataBreachProof(null)
    );
  }

  const envelopeInputs = {
    letter: target,
    packageIdentitySupport: ctx.packageIdentitySupport,
    dataBreachProof: isDataBreachLetter(target) ? dataBreachProof : null,
    perLetterAttachments: ctx.perLetterAttachments,
    packageSharedAttachments: ctx.packageSharedAttachments,
    letterId,
    ssnApprovals: ctx.ssnApprovals,
  };

  const resolvedAttachments = dedupeResolvedAttachments(
    buildEnvelopePrintableAttachments(envelopeInputs)
  );
  const pdfApiAttachments = buildPdfApiAttachments(envelopeInputs);

  return {
    resolvedAttachments,
    pdfApiAttachments,
    letterId,
    letterTarget: target,
    isDataBreach: isDataBreachLetter(target),
    fromPersisted: false,
  };
}

/** @deprecated alias — prefer resolveLetterPreviewPacket().resolvedAttachments */
export function resolveLetterPreviewAttachments(letter) {
  return resolveLetterPreviewPacket(letter).resolvedAttachments;
}

export function getLetterAttachmentCount(letter) {
  return resolveLetterPreviewPacket(letter).resolvedAttachments.length;
}

/** Letters in the same round + method package group for preview selection. */
export function getPreviewCandidatesForLetter(letters, letter) {
  if (!letter?.packageGroupKey) return letter ? [letter] : [];
  const group = letters.filter((row) => row.packageGroupKey === letter.packageGroupKey);
  return group.length ? group : letter ? [letter] : [];
}
